import type { PatchOp, PresetModel } from "@defibrillator/preset-core";
import type { VitalTargetVersion } from "@defibrillator/vital-adapter";
import type { GenerateRequestOptions, GenerationSseEvent, LocalPresetRecord } from "../../shared/types.js";
import { formatGenerationSseDebugMessage } from "./format-generation-debug-message.js";
import { composeGenerationPrompt, sanitizeGenerationOptions } from "./generation-options.js";

export type CreateGenerationMode =
  | { kind: "typed"; prompt: string; options?: GenerateRequestOptions }
  | { kind: "random"; options?: GenerateRequestOptions };

export interface CreateGenerationResult {
  latestOps: PatchOp[];
  latestModel: PresetModel | null;
  generationPrompt: string;
  jobId: string | null;
  presetId?: string;
  slug?: string;
  generationId?: string;
  versionId?: string;
  contentHash?: string;
  vitalVersion?: VitalTargetVersion;
  error: string | null;
  unauthorized: boolean;
}

export interface CreateGenerationHandlers {
  onStatus?: (status: string) => void;
  onPrompt?: (prompt: string) => void;
  onOps?: (ops: PatchOp[]) => void;
  onModel?: (model: PresetModel) => void;
  onError?: (message: string) => void;
  onDebugLog?: (source: string, message: string) => void;
}

export function buildCreateGenerationRequestBody(
  mode: CreateGenerationMode,
  idempotencyKey: string,
): unknown {
  if (mode.kind === "random") {
    return {
      random: true as const,
      idempotencyKey,
      ...(mode.options ? { options: mode.options } : {}),
    };
  }
  return {
    prompt: mode.prompt,
    idempotencyKey,
    ...(mode.options ? { options: mode.options } : {}),
  };
}

export async function runCreateGeneration(params: {
  mode: CreateGenerationMode;
  idempotencyKey: string;
  signal?: AbortSignal;
  handlers?: CreateGenerationHandlers;
}): Promise<CreateGenerationResult> {
  const { mode, idempotencyKey, signal, handlers } = params;
  const empty: CreateGenerationResult = {
    latestOps: [],
    latestModel: null,
    generationPrompt:
      mode.kind === "typed"
        ? composeGenerationPrompt(mode.prompt, sanitizeGenerationOptions(mode.options))
        : "",
    jobId: null,
    error: null,
    unauthorized: false,
  };

  const res = await fetch("/api/generate", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(buildCreateGenerationRequestBody(mode, idempotencyKey)),
    signal,
  });

  if (res.status === 401) {
    return { ...empty, unauthorized: true };
  }
  if (!res.ok || !res.body) {
    return { ...empty, error: `Generate failed (${res.status})` };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let latestOps: PatchOp[] = [];
  let latestModel: PresetModel | null = null;
  let generationPrompt =
    mode.kind === "typed"
      ? composeGenerationPrompt(mode.prompt, sanitizeGenerationOptions(mode.options))
      : "";
  let jobId: string | null = null;
  let presetId: string | undefined;
  let slug: string | undefined;
  let generationId: string | undefined;
  let versionId: string | undefined;
  let contentHash: string | undefined;
  let vitalVersion: VitalTargetVersion | undefined;
  let error: string | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split("\n\n");
    buffer = chunks.pop() ?? "";
    for (const chunk of chunks) {
      const dataLine = chunk.split("\n").find((l) => l.startsWith("data:"));
      if (!dataLine) continue;
      const event = JSON.parse(dataLine.slice(5).trim()) as GenerationSseEvent;
      if (event.type !== "status" && event.type !== "heartbeat") {
        handlers?.onDebugLog?.("sse", formatGenerationSseDebugMessage(event));
      }
      if (event.type === "status") {
        handlers?.onStatus?.(event.message);
        generationId = event.generationId;
      }
      if (event.type === "prompt") {
        generationPrompt = event.prompt;
        handlers?.onPrompt?.(event.prompt);
        handlers?.onStatus?.("prompt_ready");
        generationId = event.generationId;
      }
      if (event.type === "ops") {
        latestOps = event.ops;
        handlers?.onOps?.(event.ops);
        generationId = event.generationId;
      }
      if (event.type === "policy") {
        handlers?.onStatus?.(`policy:${event.decision.reasonCodes.join(",")}`);
      }
      if (event.type === "job") {
        jobId = event.jobId;
        handlers?.onStatus?.(`job:${event.jobId.slice(0, 8)}`);
        generationId = event.generationId;
      }
      if (event.type === "done") {
        latestModel = event.model;
        handlers?.onModel?.(event.model);
        handlers?.onStatus?.("done");
        generationId = event.generationId;
        if (event.presetId) presetId = event.presetId;
        if (event.slug) slug = event.slug;
        if (event.versionId) versionId = event.versionId;
        if (event.contentHash) contentHash = event.contentHash;
        if (event.vitalVersion) vitalVersion = event.vitalVersion;
      }
      if (event.type === "error") {
        error = event.message;
        handlers?.onError?.(event.message);
        handlers?.onStatus?.("error");
      }
    }
  }

  return {
    latestOps,
    latestModel,
    generationPrompt,
    jobId,
    presetId,
    slug,
    generationId,
    versionId,
    contentHash,
    vitalVersion,
    error,
    unauthorized: false,
  };
}

export function buildLocalPresetRecord(params: {
  result: CreateGenerationResult;
  fallbackPrompt: string;
}): LocalPresetRecord | null {
  const { result, fallbackPrompt } = params;
  if (!result.latestModel) return null;
  const id = result.presetId ?? crypto.randomUUID();
  return {
    id,
    name: result.latestModel.name,
    style: result.latestModel.style,
    slug: result.slug,
    prompt: result.generationPrompt || fallbackPrompt,
    ops: result.latestOps,
    model: result.latestModel,
    updatedAt: new Date().toISOString(),
    generationId: result.generationId,
    jobId: result.jobId ?? undefined,
    versionId: result.versionId,
    contentHash: result.contentHash,
    vitalVersion: result.vitalVersion,
  };
}
